/**
 * Pure helpers for project Realtime WebSocket diagnostic (US-075 / US-070).
 */

/** Convert HTTP(S) API base to WS(S) origin for /v1/realtime. */
export function httpBaseToWsBase(apiBaseUrl: string): string {
  const trimmed = (apiBaseUrl || "").trim().replace(/\/$/, "");
  if (!trimmed) {
    // same-origin: browsers resolve relative ws from window.location
    if (typeof window !== "undefined" && window.location) {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${proto}//${window.location.host}`;
    }
    return "ws://127.0.0.1:4000";
  }
  if (trimmed.startsWith("https://")) {
    return `wss://${trimmed.slice("https://".length)}`;
  }
  if (trimmed.startsWith("http://")) {
    return `ws://${trimmed.slice("http://".length)}`;
  }
  if (trimmed.startsWith("wss://") || trimmed.startsWith("ws://")) {
    return trimmed;
  }
  return `ws://${trimmed}`;
}

export type RealtimeConnectOpts = {
  apiBaseUrl: string;
  /** Project public or service key (query apikey). */
  apiKey: string;
  /** Optional comma-separated or array of table names. */
  tables?: string | string[];
  /** Optional app-user JWT for ownership filter re-check. */
  accessToken?: string;
};

/**
 * Build the live `/v1/realtime` WebSocket URL used by the browser.
 * Auth: apikey query (browsers cannot set custom headers on WS upgrade easily).
 */
export function buildRealtimeWsUrl(opts: RealtimeConnectOpts): string {
  const base = httpBaseToWsBase(opts.apiBaseUrl);
  const params = new URLSearchParams();
  params.set("apikey", opts.apiKey);
  if (opts.tables != null && opts.tables !== "") {
    const t = Array.isArray(opts.tables)
      ? opts.tables.map((x) => x.trim()).filter(Boolean).join(",")
      : opts.tables.trim();
    if (t) params.set("tables", t);
  }
  // accessToken cannot go on URL safely for long tokens; RealtimePage uses
  // first-message or leaves filter to public-key anonymous rules.
  // Document: backend accepts Authorization only on upgrade headers when client
  // supports it; query remains apikey-only.
  return `${base}/v1/realtime?${params.toString()}`;
}

export type RealtimeChangeMessage = {
  event: string;
  type?: string;
  table?: string;
  record?: unknown;
  old?: unknown;
};

/** Parse a hub fanout payload; returns null if not a change event. */
export function parseRealtimeMessage(raw: string): RealtimeChangeMessage | null {
  try {
    const data = JSON.parse(raw) as RealtimeChangeMessage;
    if (!data || typeof data !== "object" || typeof data.event !== "string") {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/** Summarize a change message for the diagnostic event log (no secrets). */
export function formatRealtimeEventLine(msg: RealtimeChangeMessage): string {
  const parts = [msg.event];
  if (msg.type) parts.push(msg.type);
  if (msg.table) parts.push(`table=${msg.table}`);
  return parts.join(" ");
}
